import { useEffect, useMemo, useState } from "react";
import {
  Card,
  Col,
  Row,
  Typography,
  List,
  Spin,
  Alert,
  Space,
  Button,
  Input,
  Modal,
} from "antd";
import { FilePdfOutlined, FileTextOutlined } from "@ant-design/icons";
import { db1995API } from "../services/api";

const { Title, Text } = Typography;

type CatalogItem = {
  id: number;
  file_name: string;
  case_id?: number;
  pdf_url: string;
  text_url: string;
  db1995_text_url?: string;
};

type CatalogResponse = {
  sections: Record<string, number>;
};

type SectionResponse = {
  section: string;
  total: number;
  items: CatalogItem[];
};

type SectionState = {
  open: boolean;
  loading: boolean;
  offset: number;
  total: number;
  items: CatalogItem[];
};

type SearchItem = {
  id: number;
  file_name: string;
  case_id?: number;
  reference_title?: string;
  index_name?: string;
  snippet?: string;
  highlighted_snippet?: string;
  pdf_url?: string;
  db1995_text_url?: string;
};

function renderHighlightedSnippet(text?: string) {
  const value = text || "";
  if (!value) return "Sin fragmento";
  const parts = value.split(/\[\[H\]\]|\[\[\/H\]\]/);
  return parts.map((part, index) =>
    index % 2 === 1 ? <mark key={`h-${index}`}>{part}</mark> : <span key={`t-${index}`}>{part}</span>
  );
}

const SECTION_TITLES: { key: string; title: string }[] = [
  { key: "A1", title: "PROCESO DE CAPITALIZACION ENDE" },
  { key: "A2", title: "PROCESO DE CAPITALIZACION ENTEL" },
  { key: "A3", title: "PROCESO DE CAPITALIZACION ENFE" },
  { key: "A4", title: "PROCESO DE CAPITALIZACION LAB" },
  { key: "A5", title: "PROCESO DE CAPITALIZACION YPFB" },
  { key: "A6", title: "PROCESO DE CAPITALIZACION VINTO" },
  { key: "B", title: "PROCESO DE PRIVATIZACION EN GENERAL" },
  { key: "C", title: "REFORMA DEL SISTEMA DE FONDOS DE PENSIONES" },
  { key: "D", title: "PROCESO DE REFORMA DEL SECTOR ELECTRICO" },
  { key: "E", title: "FIDEICOMISO Y CUSTODIO GLOBAL" },
  { key: "F", title: "PROCESO DE CONCESION MAESTRA DE LOS AEROPUERTOS" },
  { key: "G", title: "CONCESION DE AGUAS" },
  { key: "H", title: "FINANCIERO - CONTABLE" },
  { key: "J", title: "SISTEMA DE REGULACION SECTORIAL (SIRESE)" },
  { key: "O", title: "AUDITORIAS EXTERNA - AUDITEX" },
];

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://legal.5bc.us/api";
const ABSOLUTE_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, "");

function asAbsolute(url: string) {
  if (!url) return "#";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${ABSOLUTE_ORIGIN}${url}`;
}

export default function ReferenceCatalog() {
  const [data, setData] = useState<CatalogResponse>({ sections: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sectionState, setSectionState] = useState<Record<string, SectionState>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchItem[]>([]);
  const [quickPdfUrl, setQuickPdfUrl] = useState("");
  const [quickPdfTitle, setQuickPdfTitle] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const rows = await db1995API.getCatalog();
        setData(rows || { sections: {} });
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || "No se pudo cargar el catalogo");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const total = useMemo(() => {
    return SECTION_TITLES.reduce((sum, section) => {
      const count = data.sections?.[section.key] || 0;
      return sum + count;
    }, 0);
  }, [data]);

  const loadSection = async (sectionKey: string, reset: boolean) => {
    setSectionState((prev) => {
      const curr = prev[sectionKey] || {
        open: true,
        loading: false,
        offset: 0,
        total: data.sections?.[sectionKey] || 0,
        items: [],
      };
      return {
        ...prev,
        [sectionKey]: {
          ...curr,
          open: true,
          loading: true,
          offset: reset ? 0 : curr.offset,
          items: reset ? [] : curr.items,
        },
      };
    });

    try {
      const curr = sectionState[sectionKey];
      const response: SectionResponse = await db1995API.getCatalogSection(
        sectionKey,
        reset ? 0 : curr?.offset || 0,
        100
      );
      setSectionState((prev) => {
        const existing = prev[sectionKey] || {
          open: true,
          loading: false,
          offset: 0,
          total: response.total,
          items: [],
        };
        const mergedItems = reset ? response.items : [...existing.items, ...response.items];
        return {
          ...prev,
          [sectionKey]: {
            open: true,
            loading: false,
            offset: mergedItems.length,
            total: response.total,
            items: mergedItems,
          },
        };
      });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "No se pudo cargar la seccion");
      setSectionState((prev) => ({
        ...prev,
        [sectionKey]: {
          ...(prev[sectionKey] || {
            open: true,
            offset: 0,
            total: data.sections?.[sectionKey] || 0,
            items: [],
          }),
          loading: false,
        },
      }));
    }
  };

  const runSearch = async (value?: string) => {
    const q = (value ?? searchTerm).trim();
    if (!q) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await db1995API.search(q, 1000);
      setSearchResults(response?.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "No se pudo buscar");
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <div>
        <Title level={3} style={{ marginBottom: 0 }}>
          Catalogo de Referencias
        </Title>
        <Text type="secondary">
          Pagina principal por secciones A1-A6, B, C, D, E, F, G, H, J, O. Total archivos: {total}
        </Text>
      </div>

      {error ? <Alert type="error" showIcon message={error} /> : null}
      {loading ? <Spin /> : null}

      <Card title="Buscar en archivos (nombre + texto OCR)">
        <Space direction="vertical" style={{ width: "100%" }}>
          <Input.Search
            placeholder={'Ej: "Peter R S Earl" + "Mayo de 1994"'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={(v) => runSearch(v)}
            enterButton="Buscar"
            allowClear
            loading={searchLoading}
          />
          <Text type="secondary">
            Use + para combinar terminos (AND). Resultados: {searchResults.length}
          </Text>
          <List
            locale={{ emptyText: "Sin resultados" }}
            dataSource={searchResults}
            size="small"
            renderItem={(item) => (
              <List.Item>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Text strong>{item.file_name}</Text>
                  <Text type="secondary">
                    {item.reference_title || "-"} | {item.index_name || "-"}
                  </Text>
                  <Text>{renderHighlightedSnippet(item.highlighted_snippet || item.snippet)}</Text>
                  <Space wrap>
                    <Button
                      size="small"
                      icon={<FilePdfOutlined />}
                      href={asAbsolute(item.pdf_url || "")}
                      target="_blank"
                    >
                      PDF
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setQuickPdfTitle(item.file_name);
                        setQuickPdfUrl(asAbsolute(item.pdf_url || ""));
                      }}
                    >
                      Quick View PDF
                    </Button>
                    <Button
                      size="small"
                      icon={<FileTextOutlined />}
                      href={asAbsolute(item.db1995_text_url || "")}
                      target="_blank"
                    >
                      Texto DB1995
                    </Button>
                  </Space>
                </Space>
              </List.Item>
            )}
          />
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        {SECTION_TITLES.map((section) => {
          const count = data.sections?.[section.key] || 0;
          const state = sectionState[section.key] || {
            open: false,
            loading: false,
            offset: 0,
            total: count,
            items: [],
          };
          const hasMore = state.items.length < (state.total || count);
          return (
            <Col xs={24} lg={12} key={section.key}>
              <Card
                title={`${section.key} - ${section.title}`}
                extra={<Text type="secondary">{count} archivos</Text>}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {!state.open ? (
                    <Button onClick={() => loadSection(section.key, true)}>Abrir seccion</Button>
                  ) : null}
                  {state.open ? (
                    <>
                      <List
                        locale={{ emptyText: "Sin archivos" }}
                        dataSource={state.items}
                        size="small"
                        renderItem={(item) => (
                          <List.Item>
                            <Space direction="vertical" size={2} style={{ width: "100%" }}>
                              <Text>{item.file_name}</Text>
                              <Space wrap>
                                <Button
                                  size="small"
                                  icon={<FilePdfOutlined />}
                                  href={asAbsolute(item.pdf_url)}
                                  target="_blank"
                                >
                                  PDF
                                </Button>
                                <Button
                                  size="small"
                                  icon={<FileTextOutlined />}
                                  href={asAbsolute(item.text_url)}
                                  target="_blank"
                                >
                                  Texto Plano
                                </Button>
                                <Button
                                  size="small"
                                  icon={<FileTextOutlined />}
                                  href={asAbsolute(item.db1995_text_url || "")}
                                  target="_blank"
                                >
                                  Texto DB1995
                                </Button>
                              </Space>
                            </Space>
                          </List.Item>
                        )}
                      />
                      <Space>
                        {hasMore ? (
                          <Button loading={state.loading} onClick={() => loadSection(section.key, false)}>
                            Cargar mas
                          </Button>
                        ) : (
                          <Text type="secondary">Seccion completa</Text>
                        )}
                        <Button
                          onClick={() =>
                            setSectionState((prev) => ({
                              ...prev,
                              [section.key]: {
                                ...state,
                                open: false,
                              },
                            }))
                          }
                        >
                          Cerrar
                        </Button>
                      </Space>
                    </>
                  ) : null}
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Modal
        open={!!quickPdfUrl}
        onCancel={() => {
          setQuickPdfUrl("");
          setQuickPdfTitle("");
        }}
        footer={null}
        width={1200}
        title={`Quick View: ${quickPdfTitle}`}
        destroyOnClose
      >
        {quickPdfUrl ? (
          <iframe
            title="quick-pdf-view"
            src={quickPdfUrl}
            style={{ width: "100%", height: "75vh", border: "1px solid #ddd" }}
          />
        ) : null}
      </Modal>
    </Space>
  );
}
